import React, { useMemo, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import FacturationHeader from './FacturationHeader';
import AutocompleteSelect from 'components/AutocompleteSelect/AutocompleteSelect';
import { formatMontant, numberToWordsFr } from 'utils/numberToWordsFr';
import { toastApiErrors } from 'utils/toastApiError';
import { useGetClientQuery, } from 'actions/clients/queries';
import { useCreateClientMutation } from 'actions/clients/mutations';
import { useGetCodesQuery } from 'actions/comptabilite/queries';
import { useGetDossierByIdQuery } from 'actions/dossiers/queries';
import { useGetFacturesQuery } from 'actions/facturation/queries';
import {
  useCreateFactureMutation,
  useEmettreFactureMutation,
  useUpdateFactureMutation,
} from 'actions/facturation/mutations';
import '../ventes/client.css';

const DEFAULT_TVA_RATE = 18;
const emptyItem = {
  description: '',
  quantite: 1,
  prix_unitaire: 0,
  remise_percent: 0,
  code_comptable: null,
};

export default function FactureForm({ dossierId, type, onDone, onCancel, facture }) {
  const isEdit = !!facture;
  const isDevis = (facture?.type || type) === 'devis';
  const { data: clientsData, refetch: refetchClients } = useGetClientQuery({ dossier_id: dossierId });
  const { data: codesData } = useGetCodesQuery({ dossier_client: dossierId });
  const { data: dossierData } = useGetDossierByIdQuery({ id: dossierId });
  const { data: facturesData } = useGetFacturesQuery({ dossier_id: dossierId });
  const dossier = dossierData?.data || dossierData || null;

  // Numero previsionnel (le numero definitif est attribue par le serveur)
  const prochainNumero = useMemo(() => {
    const prefix = isDevis ? 'DEV' : 'FAC';
    const year = new Date().getFullYear();
    let max = 0;
    (facturesData?.data || []).forEach((f) => {
      const m = String(f.numero || '').match(new RegExp(`^${prefix}-${year}-(\\d+)$`));
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return `${prefix}-${year}-${String(max + 1).padStart(3, '0')}`;
  }, [facturesData, isDevis]);
  const [createClient] = useCreateClientMutation();
  const [createFacture] = useCreateFactureMutation();
  const [updateFacture] = useUpdateFactureMutation();
  const [emettreFacture] = useEmettreFactureMutation();
  const [saving, setSaving] = useState(false);
  const submitAction = React.useRef('brouillon');

  // Comptes proposables : classe 7 (produits) + 8 (HAO)
  const rawCodes = Array.isArray(codesData)
    ? codesData
    : codesData?.data || codesData?.results || [];
  const accountOptions = rawCodes
    .filter((c) => String(c.code ?? '').startsWith('7') || String(c.code ?? '').startsWith('8'))
    .map((c) => ({ id: Number(c.code), nom: c.combine || `${c.code} - ${c.libelle}` }));

  const [form, setForm] = useState({
    client: '',
    client_nom: '',
    client_email: '',
    client_telephone: '',
    client_adresse: '',
    client_ninea: '',
    date_emission: new Date().toISOString().slice(0, 10),
    date_echeance: '',
    validite_jours: 30,
    apply_tax: true,
    tva_rate: DEFAULT_TVA_RATE,
    apply_brs: false,
    brs_rate: 5,
    payment_method: '',
    payment_details: '',
    payment_terms: isDevis ? 'Devis valable 30 jours' : 'Paiement dû à la réception',
    notes: '',
  });
  const [items, setItems] = useState([{ ...emptyItem }]);

  // Hydratation en mode edition (brouillon uniquement)
  React.useEffect(() => {
    if (!facture) return;
    setForm({
      client: facture.client || '',
      client_nom: facture.client_nom || '',
      client_email: facture.client_email || '',
      client_telephone: facture.client_telephone || '',
      client_adresse: facture.client_adresse || '',
      client_ninea: facture.client_ninea || '',
      date_emission: facture.date_emission || new Date().toISOString().slice(0, 10),
      date_echeance: facture.date_echeance || '',
      validite_jours: facture.validite_jours || 30,
      apply_tax: !!facture.apply_tax,
      tva_rate: facture.tva_rate ?? DEFAULT_TVA_RATE,
      apply_brs: !!facture.apply_brs,
      brs_rate: facture.brs_rate || 5,
      payment_method: facture.payment_method || '',
      payment_details: facture.payment_details || '',
      payment_terms: facture.payment_terms || '',
      notes: facture.notes || '',
    });
    if (facture.items?.length) {
      setItems(
        facture.items.map((it) => ({
          description: it.description || '',
          quantite: it.quantite ?? 1,
          prix_unitaire: it.prix_unitaire ?? 0,
          remise_percent: it.remise_percent ?? 0,
          code_comptable: it.code_comptable ?? null,
        }))
      );
    }
  }, [facture]);

  const handleInputChange = (e) => {
    const { name, value, type: t, checked } = e.target;
    setForm((f) => ({ ...f, [name]: t === 'checkbox' ? checked : value }));
  };

  const handleClientSelect = (value) => {
    const c = (clientsData?.data || []).find((x) => x.id === Number(value));
    setForm((f) => ({
      ...f,
      client: value,
      client_nom: c?.nom || f.client_nom,
      client_email: c?.email || f.client_email,
      client_telephone: c?.telephone || f.client_telephone,
      client_adresse: c?.adresse || f.client_adresse,
    }));
  };

  const handleCreateClient = async (nom) => {
    try {
      const result = await createClient({
        nom,
        dossier_id: dossierId,
        telephone: '',
        email: '',
        adresse: '',
        active: true,
        archived: false,
        compte_comptable: '41',
        lettrage: false,
      }).unwrap();
      toast.success(`Client "${nom}" créé`);
      await refetchClients();
      setForm((f) => ({ ...f, client: result.id, client_nom: nom }));
      return result;
    } catch (error) {
      toast.error('Erreur lors de la création du client');
      throw error;
    }
  };

  const setItem = (index, key, value) => {
    setItems((arr) => arr.map((it, i) => (i === index ? { ...it, [key]: value } : it)));
  };

  const totaux = useMemo(() => {
    let brut = 0;
    let remise = 0;
    items.forEach((it) => {
      const b = (parseFloat(it.quantite) || 0) * (parseInt(it.prix_unitaire) || 0);
      brut += b;
      remise += (b * (parseInt(it.remise_percent) || 0)) / 100;
    });
    const netHt = Math.round(brut - remise);
    const tva = form.apply_tax
      ? Math.round((netHt * (parseInt(form.tva_rate) || 0)) / 100)
      : 0;
    const ttc = netHt + tva;
    const brs = form.apply_brs
      ? Math.round((netHt * (parseInt(form.brs_rate) || 0)) / 100)
      : 0;
    return {
      brut: Math.round(brut),
      remise: Math.round(remise),
      netHt,
      tva,
      ttc,
      brs,
      net: ttc - brs,
    };
  }, [items, form.apply_tax, form.tva_rate, form.apply_brs, form.brs_rate]);

  const submit = async (e) => {
    e.preventDefault();
    const lignes = [];
    for (const it of items) {
      const description = (it.description || '').trim();
      const quantite = parseFloat(it.quantite);
      const prix = parseInt(it.prix_unitaire);
      if (!description && !(quantite > 0) && !(prix > 0)) continue;
      if (!description) {
        toast.error('Renseignez la description de chaque ligne.');
        return;
      }
      if (!(quantite > 0)) {
        toast.error(`Quantité invalide pour « ${description} ».`);
        return;
      }
      const code = String(it.code_comptable ?? '').replace(/\D/g, '');
      lignes.push({
        description,
        quantite,
        prix_unitaire: prix || 0,
        remise_percent: parseInt(it.remise_percent) || 0,
        ...(code ? { code_comptable: Number(code) } : {}),
      });
    }
    if (!lignes.length) {
      toast.error('Ajoutez au moins une ligne (description, quantité, prix).');
      return;
    }
    if (!form.client_nom.trim()) {
      toast.error('Le nom du client est obligatoire.');
      return;
    }

    const payload = {
      dossier_id: parseInt(dossierId),
      type: facture?.type || type,
      ...(form.client ? { client: Number(form.client) } : {}),
      client_nom: form.client_nom.trim(),
      client_email: form.client_email || null,
      client_telephone: form.client_telephone || null,
      client_adresse: form.client_adresse || null,
      client_ninea: form.client_ninea || null,
      date_emission: form.date_emission,
      date_echeance: form.date_echeance || null,
      validite_jours: parseInt(form.validite_jours) || 30,
      apply_tax: form.apply_tax,
      tva_rate: parseInt(form.tva_rate) || DEFAULT_TVA_RATE,
      apply_brs: form.apply_brs,
      brs_rate: parseInt(form.brs_rate) || 5,
      payment_method: form.payment_method,
      payment_details: form.payment_details,
      payment_terms: form.payment_terms,
      notes: form.notes,
      items: lignes,
    };
    try {
      setSaving(true);
      const res = isEdit
        ? await updateFacture({ id: facture.id, ...payload }).unwrap()
        : await createFacture(payload).unwrap();
      const factureId = isEdit ? facture.id : res.id;
      const numero = res.numero || facture?.numero;
      if (submitAction.current === 'emit') {
        const emitRes = await emettreFacture({ id: factureId }).unwrap();
        const label = isDevis ? 'Devis' : 'Facture';
        if (emitRes?.email_envoye) {
          toast.success(`${label} ${numero} émis(e) et envoyé(e) au client par email`);
        } else {
          toast.success(
            `${label} ${numero} émis(e)` +
            (emitRes?.email_erreur ? ` : email non envoyé (${emitRes.email_erreur})` : '')
          );
        }
      } else {
        toast.success(`${isDevis ? 'Devis' : 'Facture'} ${numero} ${isEdit ? 'modifié(e)' : 'créé(e)'}`);
      }
      onDone(factureId);
    } catch (err) {
      toastApiErrors(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ backgroundColor: 'white', minHeight: '500px', borderRadius: '20px', padding: '15px' }}>
      <FacturationHeader
        nom={
          <h4 className="titre-dossier mb-0">
            {isEdit
              ? `Modifier ${facture?.numero || ''}`
              : isDevis
                ? 'Nouveau devis'
                : 'Nouvelle facture'}
          </h4>
        }
        onBack={onCancel}
      />
      <div className="w-100 w-md-75 px-0 px-md-5 container-background">
        <Container fluid className="container-style p-4 mb-5">
          <Form onSubmit={submit}>
            <div className="d-flex flex-column align-items-center gap-2">

              {/* Emetteur + numero previsionnel */}
              <div className="w-100 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="fact-emetteur" style={{ flex: '1 1 320px' }}>
                  <div className="fact-emetteur-titre">Émetteur</div>
                  <div>
                    <b>{dossier?.nom_entreprise || ':'}</b>
                    {dossier?.adresse ? ` · ${dossier.adresse}` : ''}
                  </div>
                  <div>
                    {[dossier?.telephone, dossier?.email, dossier?.imma_siret && `NINEA : ${dossier.imma_siret}`]
                      .filter(Boolean)
                      .join(' · ') || 'Complétez les informations dans Paramètres du dossier'}
                  </div>
                </div>
                <div className="fact-numero" title={isEdit ? 'Numéro du document' : "Numéro attribué automatiquement à l'enregistrement"}>
                  N° {isEdit ? facture?.numero : prochainNumero}
                </div>
              </div>

              {/* Client */}
              <div className="w-100">
                <Row>
                  <Col sm={6}>
                    <Form.Group>
                      <AutocompleteSelect
                        label="Client"
                        options={clientsData?.data || []}
                        value={form.client}
                        onChange={handleClientSelect}
                        onCreateNew={handleCreateClient}
                        placeholder="Saisissez ou sélectionnez un client"
                        createText="Créer le client"
                        type="client"
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>Nom affiché sur le document *</Form.Label>
                      <Form.Control
                        type="text"
                        name="client_nom"
                        value={form.client_nom}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              <div className="w-100">
                <Row className="row-gap-2">
                  <Col sm={3}>
                    <Form.Group>
                      <Form.Label>Adresse</Form.Label>
                      <Form.Control type="text" name="client_adresse" value={form.client_adresse}
                        onChange={handleInputChange} className="rounded rounded-pill" />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group>
                      <Form.Label>Téléphone</Form.Label>
                      <Form.Control type="text" name="client_telephone" value={form.client_telephone}
                        onChange={handleInputChange} className="rounded rounded-pill" />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="email" name="client_email" value={form.client_email}
                        onChange={handleInputChange} className="rounded rounded-pill" />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group>
                      <Form.Label>NINEA</Form.Label>
                      <Form.Control type="text" name="client_ninea" value={form.client_ninea}
                        onChange={handleInputChange} className="rounded rounded-pill" />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Dates + options */}
              <div className="w-100">
                <Row className="row-gap-2">
                  <Col sm={3}>
                    <Form.Group>
                      <Form.Label>{isDevis ? 'Date du devis' : "Date d'émission"}</Form.Label>
                      <Form.Control type="date" name="date_emission" value={form.date_emission}
                        onChange={handleInputChange} className="rounded rounded-pill" required />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group>
                      <Form.Label>{isDevis ? 'Validité (jours)' : "Date d'échéance"}</Form.Label>
                      {isDevis ? (
                        <Form.Control type="number" min="1" name="validite_jours"
                          value={form.validite_jours} onChange={handleInputChange}
                          className="rounded rounded-pill" />
                      ) : (
                        <Form.Control type="date" name="date_echeance" value={form.date_echeance}
                          onChange={handleInputChange} className="rounded rounded-pill" />
                      )}
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label style={{ fontWeight: 600 }}>Options</Form.Label>
                      <div style={{ display: 'flex', gap: '24px', marginTop: '6px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                          <input type="checkbox" name="apply_tax" checked={form.apply_tax}
                            onChange={handleInputChange} style={{ accentColor: '#009688' }} />
                          TVA
                        </label>
                        {form.apply_tax && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                            <Form.Control type="number" min="0" max="30" name="tva_rate"
                              value={form.tva_rate} onChange={handleInputChange}
                              className="rounded rounded-pill" style={{ width: 75, padding: '2px 10px' }} />
                            %
                          </label>
                        )}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                          <input type="checkbox" name="apply_brs" checked={form.apply_brs}
                            onChange={handleInputChange} style={{ accentColor: '#009688' }} />
                          Retenue BRS
                        </label>
                        {form.apply_brs && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                            <Form.Control type="number" min="1" max="30" name="brs_rate"
                              value={form.brs_rate} onChange={handleInputChange}
                              className="rounded rounded-pill" style={{ width: 75, padding: '2px 10px' }} />
                            %
                          </label>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Lignes */}
              <div className="w-100 mt-2">
                <Form.Label style={{ fontWeight: 600 }}>Lignes du document</Form.Label>
                <Row className="d-none d-md-flex mb-1" style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280' }}>
                  <Col style={{ flex: '3 1 220px', minWidth: 180 }}>Description</Col>
                  <Col style={{ flex: '0 0 100px', minWidth: 100 }}>Quantité</Col>
                  <Col style={{ flex: '0 0 140px', minWidth: 120 }}>Prix unitaire HT</Col>
                  <Col style={{ flex: '0 0 100px', minWidth: 90 }}>Remise %</Col>
                  <Col style={{ flex: '2 1 160px', minWidth: 140 }}>Compte (7xx)</Col>
                  <Col className="text-end" style={{ flex: '0 0 90px', minWidth: 80 }}>Montant HT</Col>
                  <Col style={{ flex: '0 0 48px' }} />
                </Row>
                {items.map((it, index) => (
                  <div key={index} className="facture-ligne">
                    <Row className="row-gap-2 align-items-end">
                      <Col style={{ flex: '3 1 220px', minWidth: 180 }}>
                        <Form.Control type="text" placeholder="Description de la prestation / article"
                          value={it.description}
                          onChange={(e) => setItem(index, 'description', e.target.value)}
                          className="rounded rounded-pill" />
                      </Col>
                      <Col style={{ flex: '0 0 100px', minWidth: 100 }}>
                        <Form.Control type="number" min="0" step="0.01" placeholder="Qté"
                          value={it.quantite}
                          onChange={(e) => setItem(index, 'quantite', e.target.value)}
                          className="rounded rounded-pill" title="Quantité" />
                      </Col>
                      <Col style={{ flex: '0 0 140px', minWidth: 120 }}>
                        <Form.Control type="number" min="0" placeholder="Prix unitaire HT"
                          value={it.prix_unitaire}
                          onChange={(e) => setItem(index, 'prix_unitaire', e.target.value)}
                          className="rounded rounded-pill" title="Prix unitaire HT" />
                      </Col>
                      <Col style={{ flex: '0 0 100px', minWidth: 90 }}>
                        <Form.Control type="number" min="0" max="100" placeholder="Rem. %"
                          value={it.remise_percent}
                          onChange={(e) => setItem(index, 'remise_percent', e.target.value)}
                          className="rounded rounded-pill" title="Remise %" />
                      </Col>
                      <Col style={{ flex: '2 1 160px', minWidth: 140 }}>
                        <AutocompleteSelect
                          label=""
                          options={accountOptions}
                          value={it.code_comptable}
                          onChange={(v) => setItem(index, 'code_comptable', v)}
                          onCreateNew={(txt) => {
                            const code = String(txt).replace(/\D/g, '');
                            if (!code) return null;
                            const num = Number(code);
                            setItem(index, 'code_comptable', num);
                            return { id: num, nom: txt };
                          }}
                          placeholder="Compte (7xx)"
                          createText="Utiliser ce compte"
                          type="compte"
                        />
                      </Col>
                      <Col className="text-end" style={{ flex: '0 0 90px', minWidth: 80, fontSize: '13px', alignSelf: 'center' }}>
                        {formatMontant(
                          (parseFloat(it.quantite) || 0) *
                            (parseInt(it.prix_unitaire) || 0) *
                            (1 - (parseInt(it.remise_percent) || 0) / 100)
                        )}
                      </Col>
                      <Col style={{ flex: '0 0 48px' }}>
                        <button type="button" className="fact-action-btn fact-action-red"
                          onClick={() => setItems((a) => a.filter((_, x) => x !== index))}
                          disabled={items.length === 1}>
                          ×
                        </button>
                      </Col>
                    </Row>
                  </div>
                ))}
                <button type="button" className="btn btn-outline-dark mt-2"
                  style={{ borderRadius: '31px', border: '1px solid #AEAEAE', padding: '6px 16px' }}
                  onClick={() => setItems((a) => [...a, { ...emptyItem }])}>
                  <span style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: '13px' }}>
                    + Ajouter une ligne
                  </span>
                </button>
              </div>

              {/* Reglement */}
              <div className="w-100">
                <Row className="row-gap-2">
                  <Col sm={4}>
                    <Form.Group>
                      <Form.Label>Mode de règlement</Form.Label>
                      <Form.Select name="payment_method" value={form.payment_method}
                        onChange={handleInputChange} className="rounded rounded-pill">
                        <option value="">: Sélectionner :</option>
                        <option>Espèces</option>
                        <option>Virement bancaire</option>
                        <option>Chèque</option>
                        <option>Wave</option>
                        <option>Orange Money</option>
                        <option>Autre</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col sm={4}>
                    <Form.Group>
                      <Form.Label>Coordonnées de règlement</Form.Label>
                      <Form.Control type="text" name="payment_details" value={form.payment_details}
                        placeholder="Compte, tél…" onChange={handleInputChange}
                        className="rounded rounded-pill" />
                    </Form.Group>
                  </Col>
                  <Col sm={4}>
                    <Form.Group>
                      <Form.Label>Conditions</Form.Label>
                      <Form.Control type="text" name="payment_terms" value={form.payment_terms}
                        onChange={handleInputChange} className="rounded rounded-pill" />
                    </Form.Group>
                  </Col>
                  <Col sm={12}>
                    <Form.Group>
                      <Form.Label>Notes</Form.Label>
                      <Form.Control as="textarea" rows={2} name="notes" value={form.notes}
                        onChange={handleInputChange} className="rounded" />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Totaux */}
              <div className="w-100 d-flex justify-content-end">
                <div className="facture-totaux mt-3">
                  <div><span>Sous-total HT</span><b>{formatMontant(totaux.brut)}</b></div>
                  {totaux.remise > 0 && (
                    <div><span>Remise</span><b>-{formatMontant(totaux.remise)}</b></div>
                  )}
                  <div><span>Net HT</span><b>{formatMontant(totaux.netHt)}</b></div>
                  <div><span>TVA {form.apply_tax ? `${parseInt(form.tva_rate) || 0} %` : '(désactivée)'}</span><b>{formatMontant(totaux.tva)}</b></div>
                  <div><span>Total TTC</span><b>{formatMontant(totaux.ttc)}</b></div>
                  {form.apply_brs && (
                    <div><span>BRS {form.brs_rate} %</span><b>-{formatMontant(totaux.brs)}</b></div>
                  )}
                  <div className="facture-total-final">
                    <span>Net à payer</span><b>{formatMontant(totaux.net)} FCFA</b>
                  </div>
                  <div className="text-muted fst-italic mt-1" style={{ fontSize: 12 }}>
                    {numberToWordsFr(totaux.net)}
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <Row className="mt-3 w-100">
                <Col sm={12} style={{ textAlign: 'right' }}>
                  <div className="d-flex gap-3 justify-content-end flex-wrap">
                    <button type="button" onClick={onCancel}
                      className="btn btn-danger rounded rounded-pill px-4" disabled={saving}>
                      Annuler
                    </button>
                    <button type="submit" className="btn btn-outline-dark rounded rounded-pill px-4 d-inline-flex align-items-center"
                      style={{ border: '1px solid #AEAEAE' }}
                      disabled={saving}
                      onClick={() => { submitAction.current = 'brouillon'; }}>
                      {saving && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                      {saving ? 'Enregistrement…' : 'Enregistrer le brouillon'}
                    </button>
                    <button type="submit" className="btn btn-primary rounded rounded-pill px-4 d-inline-flex align-items-center"
                      style={{ backgroundColor: '#068f7a', borderColor: '#068f7a' }}
                      disabled={saving}
                      onClick={() => { submitAction.current = 'emit'; }}>
                      {saving && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                      {saving
                        ? 'Enregistrement…'
                        : isDevis ? 'Enregistrer et envoyer' : 'Enregistrer et émettre'}
                    </button>

                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        </Container>
      </div>
    </div>
  );
}
